import { useState } from 'react'
import type { FormEvent } from 'react'
import { useForm, type FieldErrors } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff, Loader2, Mail, Lock, Building2 } from 'lucide-react'
import { Link } from 'react-router-dom'

import { AuthPageShell, LoginFlowSuccessState } from '../components/AuthShared'
import { authApi } from '../../../api/auth/auth.api'
import { showToastMessage as showAppToastMessage } from '../../../components/Toast'
import {
  forgotPasswordSchema,
  resetPasswordSchema,
  type ForgotPasswordFormData,
  type ResetPasswordFormData,
} from '../schemas/authSchema'

export function ForgotPasswordPage() {
  const otpLength = 4

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [currentStep, setCurrentStep] = useState<'request' | 'otp' | 'reset' | 'success'>('request')
  const [submittedContact, setSubmittedContact] = useState('')
  const [submittedOrgCode, setSubmittedOrgCode] = useState('')
  const [forgotResetToken, setForgotResetToken] = useState<string | null>(null)
  
  const [otpValues, setOtpValues] = useState(() => Array.from({ length: otpLength }, () => ''))
  const [otpInvalid, setOtpInvalid] = useState(false)
  const [otpError, setOtpError] = useState<string | null>(null)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)

  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false)

  // Step 1: Request Reset Form with react-hook-form + Zod
  const {
    register: registerForgot,
    handleSubmit: handleSubmitForgot,
    formState: { errors: forgotErrors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onSubmit',
    defaultValues: {
      contactValue: '',
      orgCode: '',
    },
  })

  // Step 3: Reset Password Form with react-hook-form + Zod
  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: resetErrors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onSubmit',
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  })

  const onForgotInvalid = (errors: FieldErrors<ForgotPasswordFormData>) => {
    if (errors.contactValue) {
      showAppToastMessage(errors.contactValue.message || 'Please enter a valid email or mobile number.', 'warning')
    }
  }

  const onSubmitForgot = async (data: ForgotPasswordFormData) => {
    setIsSubmitting(true)
    setSubmittedContact(data.contactValue)
    setSubmittedOrgCode(data.orgCode || '')

    try {
      await authApi.requestPasswordReset(data.contactValue, data.orgCode)
      setIsSubmitting(false)
      setOtpValues(Array.from({ length: otpLength }, () => ''))
      setOtpInvalid(false)
      setOtpError(null)
      setCurrentStep('otp')
      showAppToastMessage('OTP sent successfully. Demo OTP: 1201', 'info')
    } catch (err: unknown) {
      setIsSubmitting(false)
      const errorMessage = err instanceof Error ? err.message : 'Failed to send OTP.'
      showAppToastMessage(errorMessage, 'error')
    }
  }

  const handleOtpChange = (index: number, value: string) => {
    const nextValue = value.replace(/\D/g, '').slice(-1)

    setOtpValues((current) => {
      const next = [...current]
      next[index] = nextValue
      return next
    })
    setOtpInvalid(false)
    setOtpError(null)

    if (nextValue && index < otpLength - 1) {
      window.requestAnimationFrame(() => {
        const nextInput = document.getElementById(`forgot-otp-${index + 1}`) as HTMLInputElement | null
        nextInput?.focus()
      })
    }
  }

  const handleOtpKeyDown = (index: number, key: string) => {
    if (key === 'Backspace' && !otpValues[index] && index > 0) {
      const previousInput = document.getElementById(`forgot-otp-${index - 1}`) as HTMLInputElement | null
      previousInput?.focus()
    }
  }

  const handleVerifyOtp = async (event: FormEvent) => {
    event.preventDefault()
    if (isVerifyingOtp) return

    if (otpValues.some((value) => !value.trim())) {
      setOtpInvalid(true)
      setOtpError('Please enter the 4-digit OTP.')
      showAppToastMessage('Please enter the OTP to continue.', 'warning')
      return
    }

    const otpCode = otpValues.join('')
    setIsVerifyingOtp(true)

    try {
      const res = await authApi.verifyOtp(submittedContact, otpCode, submittedOrgCode)
      setIsVerifyingOtp(false)
      const token = res?.resetToken || (res as any)?.data?.resetToken || 'demo-reset-token'
      setForgotResetToken(token)
      showAppToastMessage('OTP Verified! Please set your new password.', 'success')
      setCurrentStep('reset')
    } catch (err: unknown) {
      setIsVerifyingOtp(false)
      setOtpInvalid(true)
      const errorMessage = err instanceof Error ? err.message : 'Oops! Entered OTP is incorrect. Please try again.'
      setOtpError(errorMessage)
      showAppToastMessage(errorMessage, 'warning')
    }
  }

  const handleResendOtp = () => {
    setOtpValues(Array.from({ length: otpLength }, () => ''))
    setOtpInvalid(false)
    setOtpError(null)
    showAppToastMessage('A new OTP has been sent successfully.', 'info')
    window.requestAnimationFrame(() => {
      const firstInput = document.getElementById('forgot-otp-0') as HTMLInputElement | null
      firstInput?.focus()
    })
  }

  const onResetInvalid = (errors: FieldErrors<ResetPasswordFormData>) => {
    if (errors.newPassword) {
      showAppToastMessage(errors.newPassword.message || 'Please enter the new password.', 'warning')
    } else if (errors.confirmPassword) {
      showAppToastMessage(errors.confirmPassword.message || 'Please confirm the new password.', 'warning')
    }
  }

  const onSubmitReset = async (data: ResetPasswordFormData) => {
    setIsUpdatingPassword(true)

    try {
      await authApi.confirmPasswordReset(data.newPassword, forgotResetToken || undefined)
      setIsUpdatingPassword(false)
      showAppToastMessage('Password updated successfully!', 'success')
      setCurrentStep('success')
    } catch (err: unknown) {
      setIsUpdatingPassword(false)
      const errorMessage = err instanceof Error ? err.message : 'Failed to update password.'
      showAppToastMessage(errorMessage, 'error')
    }
  }

  return (
    <AuthPageShell>
      <div className="w-full">
        {currentStep === 'otp' ? (
          <div className="flex w-full flex-col items-center text-center">
            <h2 className="text-lg font-bold text-gray-900">
              Enter OTP
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              We have sent an OTP to your mobile number/ mail id
            </p>

            <form onSubmit={handleVerifyOtp} className="mt-6 w-full">
              <div className="flex items-center justify-center gap-2.5">
                {otpValues.map((value, index) => (
                  <div key={`forgot-otp-${index}`} className="flex items-center gap-2.5">
                    <input
                      id={`forgot-otp-${index}`}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={1}
                      value={value}
                      onChange={(event) => handleOtpChange(index, event.target.value)}
                      onKeyDown={(event) => handleOtpKeyDown(index, event.key)}
                      className={`h-11 w-12 rounded-lg border bg-white text-center text-base font-semibold text-gray-800 shadow-sm outline-none transition focus:ring-2 ${
                        otpInvalid
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                          : 'border-gray-200 focus:border-[#146971] focus:ring-[#146971]/20'
                      }`}
                    />
                    {index < otpLength - 1 && (
                      <span aria-hidden className="text-gray-300 font-bold">
                        -
                      </span>
                    )}
                  </div>
                ))}
              </div>



              <button
                type="submit"
                disabled={isVerifyingOtp}
                className="mt-6 flex w-full bg-[#146971] hover:bg-[#0f545a] text-white font-medium py-2.5 rounded-lg text-sm transition-colors shadow-sm items-center justify-center disabled:cursor-not-allowed disabled:opacity-80 cursor-pointer"
              >
                {isVerifyingOtp ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify OTP'}
              </button>
            </form>

            <p className="mt-4 flex items-center justify-center gap-1.5 text-xs font-medium text-gray-600">
              <span>Didn’t receive code?</span>
              <button
                type="button"
                onClick={handleResendOtp}
                className="bg-transparent p-0 font-bold text-[#146971] hover:underline cursor-pointer"
              >
                Resend OTP
              </button>
            </p>
          </div>
        ) : currentStep === 'reset' ? (
          <div className="w-full text-left">
            <h2 className="text-lg font-bold text-gray-900 text-center">
              Set a New Password
            </h2>
            <p className="mt-1 text-xs text-gray-500 text-center mb-6">
              Your new password must be different from previously used passwords
            </p>

            <form onSubmit={handleSubmitReset(onSubmitReset, onResetInvalid)} className="space-y-4 font-sans">
              <div>
                <label
                  htmlFor="new-password"
                  className="mb-1.5 block text-xs font-semibold text-gray-700"
                >
                  New Password
                </label>
                <div className="relative flex items-center">
                  <Lock className="pointer-events-none absolute left-3.5 h-4 w-4 text-gray-400" />
                  <input
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    {...registerReset('newPassword')}
                    className={`w-full h-[46px] rounded-lg border bg-white pl-10 pr-10 text-sm placeholder:text-gray-400 outline-none transition focus:ring-2 ${
                      resetErrors.newPassword
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                        : 'border-gray-200 focus:border-[#146971] focus:ring-[#146971]/20'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword((current) => !current)}
                    className="absolute right-3.5 flex items-center text-gray-400 hover:text-gray-600"
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {resetErrors.newPassword && (
                  <p className="mt-1 text-xs font-medium text-red-500">
                    {resetErrors.newPassword.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="confirm-password"
                  className="mb-1.5 block text-xs font-semibold text-gray-700"
                >
                  Confirm Password
                </label>
                <div className="relative flex items-center">
                  <Lock className="pointer-events-none absolute left-3.5 h-4 w-4 text-gray-400" />
                  <input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Re-enter new password"
                    {...registerReset('confirmPassword')}
                    className={`w-full h-[46px] rounded-lg border bg-white pl-10 pr-10 text-sm placeholder:text-gray-400 outline-none transition focus:ring-2 ${
                      resetErrors.confirmPassword
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                        : 'border-gray-200 focus:border-[#146971] focus:ring-[#146971]/20'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((current) => !current)}
                    className="absolute right-3.5 flex items-center text-gray-400 hover:text-gray-600"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {resetErrors.confirmPassword && (
                  <p className="mt-1 text-xs font-medium text-red-500">
                    {resetErrors.confirmPassword.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isUpdatingPassword}
                className="mt-6 flex h-[46px] w-full bg-[#146971] hover:bg-[#0f545a] text-white font-medium py-2.5 rounded-lg text-sm transition-colors shadow-sm items-center justify-center disabled:cursor-not-allowed disabled:opacity-80 cursor-pointer"
              >
                {isUpdatingPassword ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update Password'}
              </button>
            </form>
          </div>
        ) : currentStep === 'success' ? (
          <div className="flex w-full flex-col items-center text-center">
            <LoginFlowSuccessState
              imageAlt="Password updated successfully"
              message="Password set successfully. continue the"
              linkTo="/login"
              linkLabel="login"
            />
          </div>
        ) : (
          <div className="w-full text-left">
            <h2 className="text-lg font-bold text-gray-900 text-center">
              Forgot password?
            </h2>
            <p className="mt-1 text-xs text-gray-500 text-center mb-6">
              Enter your email address and we&apos;ll send an OTP to reset password
            </p>

            <form onSubmit={handleSubmitForgot(onSubmitForgot, onForgotInvalid)} className="space-y-4 font-sans">
              <div>
                <label
                  htmlFor="reset-email"
                  className="mb-1.5 block text-xs font-semibold text-gray-700"
                >
                  Email Address
                </label>
                <div className="relative flex items-center">
                  <Mail className="pointer-events-none absolute left-3.5 h-4 w-4 text-gray-400" />
                  <input
                    id="reset-email"
                    type="text"
                    placeholder="example@gmail.com"
                    {...registerForgot('contactValue')}
                    className={`w-full h-[46px] rounded-lg border bg-white pl-10 pr-3 text-sm placeholder:text-gray-400 outline-none transition focus:ring-2 ${
                      forgotErrors.contactValue
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                        : 'border-gray-200 focus:border-[#146971] focus:ring-[#146971]/20'
                    }`}
                  />
                </div>
                {forgotErrors.contactValue && (
                  <p className="mt-1 text-xs font-medium text-red-500">
                    {forgotErrors.contactValue.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-6 flex w-full bg-[#146971] hover:bg-[#0f545a] text-white font-medium py-2.5 rounded-lg text-sm transition-colors shadow-sm items-center justify-center disabled:cursor-not-allowed disabled:opacity-80 cursor-pointer"
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Reset Link'}
              </button>
            </form>

            <p className="mt-6 text-center text-xs font-medium text-gray-600">
              Go back to{' '}
              <Link
                to="/login"
                className="font-semibold text-[#146971] hover:underline"
              >
                Login
              </Link>
            </p>
          </div>
        )}
      </div>
    </AuthPageShell>
  )
}
